import { getLayoutTokens } from '@/lib/cv-layout-styles';
import { NativeButton } from '@/components/common/NativeButton';
import { usePhotoPicker, releasePhotoAsset } from '@/components/photos/usePhotoPicker';
import type { UploadableImageAsset } from '@/lib/image-upload';
import { useEffect, useRef, useState } from "react";
import { useToast } from "../../../hooks/useToast";
import { useRouter } from "expo-router";
import { useTranslation } from "react-i18next";
import {
  AtSign,
  Briefcase,
  Camera,
  Info,
  Mail,
  Pencil,
  Plus,
  User,
  X,
} from "lucide-react";
import { colors } from "@tabbio/tokens";
import {
  collapseCvLinkInput,
  composeCvLink,
  getCvLinkInputPrefix,
  getCvLinkInputValue,
  validateCvLinkInput,
  type CvLinkKey,
} from "@tabbio/utils/cv-profile-links";
import { isValidEmail, isValidPhone, MAX_LINK_URL } from "@tabbio/utils";
import { useAuth } from "../../../hooks/useAuth";
import { useCVData } from "../../../lib/cv-mock-data";
import { useUaePassStatus } from "../../../hooks/useUaePassStatus";
import { WebPhoneInput, WebLocationPicker } from "./web-pickers";
import { uploadImageAsset } from "../../../lib/image-upload";
import { SpinnerGlyph } from "../../common/SpinnerGlyph.web";
import { LinkedinIcon } from "../../common/BrandIcons";
import { getPublicSiteLabel } from "../../../lib/public-web";
import { useUsernameEditor } from "../../../hooks/useUsernameEditor";
import { confirmUsernameChange } from "../../../lib/username-change-confirmation";
import { useEntitlement } from "../../../providers/SubscriptionProvider.shared";
import { useUpgradeModal } from "../../../providers/UpgradeModalProvider";
import { confirmDeleteCvItem } from "./delete-confirmation";
import { cvChangeNotApplied } from "../../../lib/cv-write-coordinator";

const INPUT: React.CSSProperties = {
  width: "100%",
  height: 52,
  borderRadius: 9999,
  border: "1px solid var(--input-border)",
  padding: "0 22px",
  fontSize: 15,
  color: "var(--text-primary)",
  backgroundColor: "var(--input-bg)",
  boxShadow: "var(--gem-input-shadow)",
  fontFamily: "inherit",
  outline: "none",
  boxSizing: "border-box",
};
const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 8,
  display: "block",
};
const FIELD_LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--text-secondary)",
  marginBottom: 8,
  display: "block",
};
const LINK_PLATFORMS: {
  key: CvLinkKey;
  label: string;
  prefix: string;
  placeholder: string;
  icon?: (color: string) => React.ReactNode;
  brandColor?: string;
}[] = [
  {
    key: "linkedin",
    label: "LinkedIn",
    prefix: getCvLinkInputPrefix("linkedin"),
    placeholder: "username",
    icon: (color) => <LinkedinIcon size={15} color={color} />,
    brandColor: "#0A66C2",
  },
  {
    key: "github",
    label: "GitHub",
    prefix: getCvLinkInputPrefix("github"),
    placeholder: "username",
  },
  {
    key: "website",
    label: "Website",
    prefix: getCvLinkInputPrefix("website"),
    placeholder: "yoursite.com",
  },
  {
    key: "dribbble",
    label: "Dribbble",
    prefix: getCvLinkInputPrefix("dribbble"),
    placeholder: "username",
  },
  {
    key: "behance",
    label: "Behance",
    prefix: getCvLinkInputPrefix("behance"),
    placeholder: "username",
  },
];

interface EditHeaderFormProps {
  onSave?: () => Promise<void> | void;
  usernameMode?: "editable" | "readonly";
  usernameReadOnlyMessage?: string;
}

function FieldLabelWithInfo({
  htmlFor,
  label,
  info,
}: {
  htmlFor: string;
  label: string;
  info: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginBottom: 6,
        position: "relative",
      }}
    >
      <label style={{ ...FIELD_LABEL, marginBottom: 0 }} htmlFor={htmlFor}>
        {label}
      </label>
      <button
        type="button"
        className="cv-header-info-button"
        aria-label={`${label} info`}
        data-tooltip={info}
      >
        <Info size={13} strokeWidth={2.2} />
      </button>
    </div>
  );
}

/** Pro badge on a locked control. Mirrors CustomizeCardSheet.web's badge. */
function UpgradeBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        backgroundImage: `linear-gradient(90deg, ${colors.brandVioletLight}, ${colors.brandViolet})`,
        color: "#fff",
        borderRadius: 20,
        padding: "3px 8px",
        fontSize: 10,
        fontWeight: 700,
        textTransform: "uppercase",
      }}
    >
      {label}
    </span>
  );
}

function TabbioLinkSwitch({
  value,
  onChange,
  ariaLabel,
}: {
  value: boolean;
  onChange: (next: boolean) => void;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      aria-label={ariaLabel}
      onClick={() => onChange(!value)}
      style={{
        position: "relative",
        width: 44,
        height: 26,
        borderRadius: 9999,
        border: 0,
        cursor: "pointer",
        flexShrink: 0,
        background: value
          ? colors.brandViolet
          : "var(--hover-strong, rgba(0,0,0,0.1))",
        transition: "background-color 150ms",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 2,
          insetInlineStart: value ? 20 : 2,
          width: 22,
          height: 22,
          borderRadius: 11,
          background: "#ffffff",
          transition: "inset-inline-start 150ms",
        }}
      />
    </button>
  );
}

export function EditHeaderForm({
  onSave,
  usernameMode = "editable",
  usernameReadOnlyMessage,
}: EditHeaderFormProps) {
  const { t } = useTranslation("seeker");
  const { t: tAuth } = useTranslation("auth");
  const { t: tCommon } = useTranslation("common");
  const { isUaePassVerified } = useAuth();
  const {
    profile,
    updateProfile,
    updateCVConfig,
    setCVHideTabbioLink,
    cvConfig,
    flushCVConfigSave,
    target,
  } = useCVData();
  const { data: uaePassStatus } = useUaePassStatus(isUaePassVerified);
  const router = useRouter();
  const [isSaving, setIsSaving] = useState(false);
  const toast = useToast();
  const usernameEditor = useUsernameEditor((key, options) =>
    String(tCommon(key, options as any)),
  );
  const currentUsernameInput = usernameEditor.username;
  const setCurrentUsernameInput = usernameEditor.setUsername;

  const uaePassMobile = uaePassStatus?.uaePassData?.mobile || "";
  const [name, setName] = useState(profile.name || "");
  const [headline, setHeadline] = useState(profile.headline || "");
  const [nameError, setNameError] = useState("");
  const [cvEmail, setCvEmail] = useState(profile.contactEmail || "");
  const [phone, setPhone] = useState(profile.phone || "");
  const [phoneError, setPhoneError] = useState("");
  const [location, setLocation] = useState(profile.location || "");
  const [avatarUrl, setAvatarUrl] = useState(profile.avatarUrl || "");
  const [showTabbioLink, setShowTabbioLink] = useState(
    cvConfig.hideTabbioLink !== true,
  );
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const { pickPhoto, cropper, isPicking } = usePhotoPicker({ kind: 'cv', previewShape: getLayoutTokens(cvConfig.style).photoShape });
  const photoGeneration = useRef(0);
  useEffect(() => () => { photoGeneration.current += 1; }, []);
  const [links, setLinks] = useState<Record<CvLinkKey, string | undefined>>({
    linkedin: getCvLinkInputValue("linkedin", profile.linkedin || undefined),
    github: getCvLinkInputValue("github", profile.github || undefined),
    website: getCvLinkInputValue("website", profile.website || undefined),
    dribbble: getCvLinkInputValue("dribbble", profile.dribbble || undefined),
    behance: getCvLinkInputValue("behance", profile.behance || undefined),
  });

  const synced = useRef(false);
  useEffect(() => {
    if (synced.current) return;
    const hasData =
      profile.name ||
      profile.headline ||
      profile.contactEmail ||
      profile.phone ||
      profile.location ||
      profile.linkedin ||
      profile.website ||
      profile.avatarUrl;
    if (!hasData) return;
    synced.current = true;
    if (profile.name) setName(profile.name);
    if (profile.headline) setHeadline(profile.headline);
    if (profile.contactEmail) setCvEmail(profile.contactEmail);
    if (profile.phone) setPhone(profile.phone);
    if (profile.location) setLocation(profile.location);
    if (profile.avatarUrl) setAvatarUrl(profile.avatarUrl);
    setLinks((prev) => ({
      ...prev,
      ...(profile.linkedin
        ? { linkedin: getCvLinkInputValue("linkedin", profile.linkedin) }
        : {}),
      ...(profile.github
        ? { github: getCvLinkInputValue("github", profile.github) }
        : {}),
      ...(profile.website
        ? { website: getCvLinkInputValue("website", profile.website) }
        : {}),
      ...(profile.dribbble
        ? { dribbble: getCvLinkInputValue("dribbble", profile.dribbble) }
        : {}),
      ...(profile.behance
        ? { behance: getCvLinkInputValue("behance", profile.behance) }
        : {}),
    }));
  }, [profile]);

  useEffect(() => {
    if (
      usernameMode !== "editable" ||
      target ||
      !profile.username ||
      currentUsernameInput.trim()
    ) {
      return;
    }
    setCurrentUsernameInput(profile.username);
  }, [
    currentUsernameInput,
    profile.username,
    setCurrentUsernameInput,
    target,
    usernameMode,
  ]);

  useEffect(() => {
    if (!isUaePassVerified || !uaePassMobile) {
      return;
    }

    setPhone((prev) => prev || uaePassMobile);
    setPhoneError("");
  }, [isUaePassVerified, uaePassMobile]);

  const addedLinks = LINK_PLATFORMS.filter(
    (platform) => links[platform.key] !== undefined,
  );
  const unaddedLinks = LINK_PLATFORMS.filter(
    (platform) => links[platform.key] === undefined,
  );
  const readonlyUsernameMessage =
    usernameReadOnlyMessage ??
    t("cv.edit.usernameReadOnlyShared", {
      defaultValue: "The owner username cannot be changed from a shared CV.",
    });
  const canEditUsername = usernameMode === "editable" && !target;
  const resolvedPhone = phone.trim();
  const publicSiteLabel = getPublicSiteLabel() || "tabbio.com";
  const usernameValue = canEditUsername
    ? currentUsernameInput
    : profile.username;
  const usernamePreview = (
    canEditUsername ? usernameEditor.normalizedUsername : profile.username
  ).trim();
  const usernameStatusMessage = canEditUsername
    ? usernameEditor.usernameStatus.message
    : readonlyUsernameMessage;
  const usernameStatusTone = canEditUsername
    ? usernameEditor.usernameStatus.tone
    : "neutral";
  const usernameStatusColor =
    usernameStatusTone === "success"
      ? colors.success
      : usernameStatusTone === "error"
        ? "#d93025"
        : "var(--text-tertiary)";
  const usernameBorderColor =
    usernameStatusTone === "error" ? "#d93025" : undefined;
  // Pro opt-out for the `{handle}.tabbio.com` row this CV prints. Free plans
  // see the switch locked on; clicking it opens the paywall dialog. The server
  // re-applies the same gate on save and on every read.
  const brandingDecision = useEntitlement("individual.custom_branding");
  const canHideTabbioLink = brandingDecision.allowed;
  const upgrade = useUpgradeModal();
  const openBrandingUpgrade = () =>
    upgrade.open({
      placement: "branding",
      entryPoint: "cv.header_tabbio_link",
      planSet: "individual",
      recommendedTier: brandingDecision.recommendedTier ?? "plus",
      featureKey: "individual.custom_branding",
      reason: brandingDecision.reasonCode,
    });

  const usernameSaveBlocked =
    canEditUsername &&
    usernameEditor.usernameChanged &&
    !usernameEditor.canSaveUsername;
  const phoneRequiredMessage = tAuth("onboarding.cvJourney.phoneRequired", {
    defaultValue: "Phone number is required.",
  });
  const phoneInvalidMessage = tAuth("onboarding.cvJourney.phoneInvalid", {
    defaultValue:
      "Enter a valid phone number (7–15 digits, optional leading +).",
  });

  const fieldErrors: { cvEmail?: string } & Partial<Record<CvLinkKey, string>> =
    {};
  if (cvEmail.trim() && !isValidEmail(cvEmail.trim())) {
    fieldErrors.cvEmail = t("cv.edit.invalidEmail");
  }
  for (const platform of LINK_PLATFORMS) {
    const v = (links[platform.key] || "").trim();
    if (!v) continue;
    if (v.length > MAX_LINK_URL) {
      fieldErrors[platform.key] = t("cv.edit.tooLong", { max: MAX_LINK_URL });
    } else if (validateCvLinkInput(platform.key, v)) {
      fieldErrors[platform.key] = t("cv.edit.invalidUrl");
    }
  }
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  // Format errors fire live as the user types. Required errors still wait
  // for save attempt: see handleSave.
  const visibleErrors = fieldErrors;

  const trimmedPhone = phone.trim();
  const livePhoneError =
    trimmedPhone && !isValidPhone(trimmedPhone) ? phoneInvalidMessage : "";
  const displayedPhoneError = phoneError || livePhoneError;

  const handleAvatarChange = async () => {
    const generation = photoGeneration.current;
    let asset: UploadableImageAsset | null = null;
    try {
      asset = await pickPhoto();
      if (!asset || generation !== photoGeneration.current) return;
      setIsUploadingAvatar(true);
      const uploadedUrl = await uploadImageAsset(asset, {
        kind: "profile-avatar",
        fileName: asset.name,
        mimeType: asset.type,
      });
      if (generation !== photoGeneration.current) return;
      setAvatarUrl(uploadedUrl);
    } catch (error) {
      toast.error(
        tAuth("onboarding.profileSetup.photoUploadFailed", {
          defaultValue: "Could not upload your photo",
        }),
        error instanceof Error ? error.message : undefined,
      );
    } finally {
      if (asset) void releasePhotoAsset(asset);
      if (generation === photoGeneration.current) setIsUploadingAvatar(false);
    }
  };

  const handleSave = async () => {
    const trimmedName = name.trim();
    if (!trimmedName) {
      setNameError(
        t("cv.edit.nameRequired", { defaultValue: "Full name is required" }),
      );
      return;
    }
    if (usernameSaveBlocked) {
      return;
    }
    if (!resolvedPhone) {
      setPhoneError(phoneRequiredMessage);
      return;
    }
    if (!isValidPhone(resolvedPhone)) {
      setPhoneError(phoneInvalidMessage);
      return;
    }
    if (hasFieldErrors) {
      return;
    }

    // Renaming frees the old {handle} link at once; confirm before saving.
    if (
      canEditUsername &&
      usernameEditor.usernameChanged &&
      !(await confirmUsernameChange(
        (key, options) => String(tCommon(key, options as any)),
        {
          from: usernameEditor.currentUsername,
          to: usernameEditor.normalizedUsername,
        },
      ))
    ) {
      return;
    }

    setPhoneError("");
    setNameError("");
    setIsSaving(true);
    const previousProfile = { ...profile };
    try {
      let savedUsername = profile.username;
      if (canEditUsername && usernameEditor.usernameChanged) {
        const nextUsername = await usernameEditor.saveUsername();
        if (!nextUsername) {
          return;
        }
        savedUsername = nextUsername;
      } else if (canEditUsername && usernameEditor.normalizedUsername) {
        savedUsername = usernameEditor.normalizedUsername;
      }

      updateProfile({
        name: trimmedName,
        username: savedUsername,
        headline: headline.trim(),
        contactEmail: cvEmail.trim(),
        phone: resolvedPhone,
        location,
        avatarUrl,
        linkedin: composeCvLink("linkedin", links.linkedin),
        website: composeCvLink("website", links.website),
        github: composeCvLink("github", links.github),
        dribbble: composeCvLink("dribbble", links.dribbble),
        behance: composeCvLink("behance", links.behance),
      });

      // Only a Pro plan can turn the link off; a free account's switch is
      // locked on, so `showTabbioLink` is already true there.
      const nextHideTabbioLink = canHideTabbioLink && !showTabbioLink;
      const hideTabbioLinkChanged =
        nextHideTabbioLink !== (cvConfig.hideTabbioLink === true);

      if (onSave) {
        await onSave();
        // Applied after the profile save and flushed at once, like the photo
        // toggle below: the config save is otherwise debounced, and leaving
        // the CV layout (router.back) would drop the pending write.
        if (hideTabbioLinkChanged) {
          setCVHideTabbioLink(nextHideTabbioLink);
          flushCVConfigSave();
        }
        if (avatarUrl !== previousProfile.avatarUrl) {
          updateCVConfig({ showPhoto: Boolean(avatarUrl) });
          flushCVConfigSave();
        }
        toast.success(t("cv.edit.saved"));
        if (router.canGoBack()) router.back();
      } else {
        if (hideTabbioLinkChanged) {
          setCVHideTabbioLink(nextHideTabbioLink);
          flushCVConfigSave();
        }
        router.back();
      }
    } catch (error) {
      // B02: a CV conflict the user answered is not a failure. The editor
      // already shows the latest version (fetched before the prompt), so it
      // is never rolled back to the pre-save copy. "Discard" closes; "keep
      // editing" keeps this draft open.
      const outcome = cvChangeNotApplied(error);
      if (outcome) {
        if (outcome === "discarded" && router.canGoBack()) router.back();
        return;
      }
      updateProfile(previousProfile);
      toast.error(t("cv.edit.saveFailed"));
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitDisabled =
    isUploadingAvatar || isPicking ||
    isSaving ||
    usernameEditor.isSavingUsername ||
    hasFieldErrors ||
    Boolean(livePhoneError) ||
    usernameSaveBlocked ||
    !name.trim();

  return (
    <div>
      {cropper}

      <div
        style={{ marginBottom: 24, display: "flex", justifyContent: "center" }}
      >
        <button
          type="button"
          onClick={() => void handleAvatarChange()}
          disabled={isUploadingAvatar || isPicking}
          aria-label={
            avatarUrl
              ? tAuth("onboarding.profileSetup.changePhoto", {
                  defaultValue: "Change photo",
                })
              : tAuth("onboarding.profileSetup.uploadPhoto", {
                  defaultValue: "Upload photo",
                })
          }
          style={{
            position: "relative",
            width: 104,
            height: 104,
            borderRadius: "50%",
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: isUploadingAvatar ? "default" : "pointer",
            flexShrink: 0,
            fontFamily: "inherit",
          }}
        >
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt=""
              style={{
                width: 104,
                height: 104,
                borderRadius: "50%",
                objectFit: "cover",
                display: "block",
              }}
            />
          ) : (
            <div
              style={{
                width: 104,
                height: 104,
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.04)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Camera size={28} color="var(--text-tertiary)" />
            </div>

          )}
          <div
            style={{
              position: "absolute",
              right: 0,
              bottom: 0,
              width: 32,
              height: 32,
              borderRadius: "50%",
              backgroundColor: "var(--text-primary)",
              border: "2px solid var(--bg)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Pencil size={14} color="#fff" strokeWidth={2.5} />
          </div>
          {isUploadingAvatar ? (
            <div
              style={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                backgroundColor: "rgba(0,0,0,0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SpinnerGlyph color="#fff" size={20} />
            </div>
          ) : null}
        </button>
      </div>
      {avatarUrl ? <NativeButton title={tCommon('accountPhoto.removeCv')} variant="ghost" color="danger"
        disabled={isUploadingAvatar || isPicking || isSaving} onPress={() => setAvatarUrl('')} /> : null}


      <div
        style={{
          marginBottom: 24,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <span style={LABEL}>
          {t("cv.edit.identity", { defaultValue: "Identity" })}
        </span>
        <div>
          <label style={FIELD_LABEL} htmlFor="cv-name">
            {t("cv.edit.name")}
          </label>
          <div style={{ position: "relative" }}>
            <User
              size={16}
              color="var(--text-tertiary)"
              style={{
                position: "absolute",
                insetInlineStart: 18,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              id="cv-name"
              style={{
                ...INPUT,
                paddingInlineStart: 46,
                ...(nameError ? { border: "1px solid #d93025" } : {}),
              }}
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (event.target.value.trim()) setNameError("");
              }}
              placeholder={t("cv.edit.name")}
              autoComplete="name"
              readOnly={false}
              aria-readonly={false}
              aria-invalid={Boolean(nameError)}
              aria-describedby={nameError ? "cv-name-error" : undefined}
            />
          </div>
          {nameError ? (
            <p
              id="cv-name-error"
              style={{
                margin: "6px 0 0",
                fontSize: 13,
                lineHeight: "18px",
                color: "#d93025",
              }}
            >
              {nameError}
            </p>
          ) : null}
        </div>
        <div>
          <label style={FIELD_LABEL} htmlFor="cv-headline">
            {t("cv.edit.headline")}
          </label>
          <div style={{ position: "relative" }}>
            <Briefcase
              size={16}
              color="var(--text-tertiary)"
              style={{
                position: "absolute",
                insetInlineStart: 18,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              id="cv-headline"
              style={{ ...INPUT, paddingInlineStart: 46 }}
              value={headline}
              onChange={(event) => setHeadline(event.target.value)}
              placeholder={t("cv.edit.headline")}
            />
          </div>
        </div>
        <div>
          <FieldLabelWithInfo
            htmlFor="cv-username"
            label={tCommon("settings.accountEdit.usernameTitle")}
            info={
              canEditUsername
                ? tCommon("settings.accountEdit.usernameDescription")
                : readonlyUsernameMessage
            }
          />
          <div style={{ position: "relative" }}>
            <AtSign
              size={16}
              color="var(--text-tertiary)"
              style={{
                position: "absolute",
                insetInlineStart: 18,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              id="cv-username"
              style={{
                ...INPUT,
                paddingInlineStart: 46,
                ...(usernameBorderColor
                  ? { border: `1px solid ${usernameBorderColor}` }
                  : {}),
                ...(!canEditUsername ? { color: "var(--text-secondary)" } : {}),
              }}
              value={usernameValue}
              onChange={(event) =>
                setCurrentUsernameInput(event.target.value.toLowerCase())
              }
              placeholder={tCommon("settings.accountEdit.usernamePlaceholder")}
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              disabled={!canEditUsername}
              aria-invalid={usernameStatusTone === "error"}
              aria-describedby="cv-username-status"
            />
          </div>
          <p
            id="cv-username-status"
            style={{
              margin: "6px 0 0",
              fontSize: 12,
              lineHeight: "18px",
              color: usernameStatusColor,
            }}
          >
            {usernamePreview
              ? `${usernamePreview}.${publicSiteLabel} · ${usernameStatusMessage}`
              : usernameStatusMessage}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginTop: 4,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            >
              <span style={{ fontSize: 13, fontWeight: 600 }}>
                {t("cv.edit.showTabbioLink", {
                  defaultValue: "Show Tabbio link",
                })}
              </span>
              {canHideTabbioLink ? null : (
                <UpgradeBadge
                  label={t("cv.customizeCard.upgradeBadge", {
                    defaultValue: "Upgrade",
                  })}
                />
              )}
            </div>
            <p
              style={{
                margin: "2px 0 0",
                fontSize: 12,
                lineHeight: "18px",
                color: "var(--text-tertiary)",
              }}
            >
              {t("cv.edit.showTabbioLinkHint", {
                defaultValue:
                  "Prints your Tabbio address in the CV header. Turn it off to keep the CV unbranded.",
              })}
            </p>
          </div>
          <TabbioLinkSwitch
            value={canHideTabbioLink ? showTabbioLink : true}
            ariaLabel={t("cv.edit.showTabbioLink", {
              defaultValue: "Show Tabbio link",
            })}
            onChange={
              canHideTabbioLink ? setShowTabbioLink : openBrandingUpgrade
            }
          />
        </div>
      </div>

      <div
        style={{
          marginBottom: 24,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <span style={LABEL}>
          {tAuth("onboarding.cvJourney.contactSection", {
            defaultValue: "CONTACT",
          })}
        </span>
        <div>
          <FieldLabelWithInfo
            htmlFor="cv-contact-email"
            label={tAuth("onboarding.cvJourney.cvEmailLabel", {
              defaultValue: "CV contact email (optional)",
            })}
            info={tAuth("onboarding.cvJourney.cvEmailHelper", {
              defaultValue:
                "Use this email on your CV if you want people to contact you there. Leave it blank to hide an email from your CV.",
            })}
          />
          <div style={{ position: "relative" }}>
            <Mail
              size={16}
              color="var(--text-tertiary)"
              style={{
                position: "absolute",
                insetInlineStart: 18,
                top: "50%",
                transform: "translateY(-50%)",
              }}
            />
            <input
              id="cv-contact-email"
              style={{
                ...INPUT,
                paddingInlineStart: 46,
                ...(visibleErrors.cvEmail
                  ? { border: `1px solid ${"#d93025"}` }
                  : {}),
              }}
              value={cvEmail}
              onChange={(event) => setCvEmail(event.target.value)}
              placeholder={tAuth("onboarding.cvJourney.cvEmailPlaceholder", {
                defaultValue: "email@example.com",
              })}
              type="email"
              autoComplete="email"
              aria-invalid={Boolean(visibleErrors.cvEmail)}
              aria-describedby={
                visibleErrors.cvEmail ? "cv-contact-email-error" : undefined
              }
            />
          </div>
          {visibleErrors.cvEmail ? (
            <p
              id="cv-contact-email-error"
              style={{
                margin: "6px 0 0",
                fontSize: 13,
                lineHeight: "18px",
                color: "#d93025",
              }}
            >
              {visibleErrors.cvEmail}
            </p>
          ) : null}
        </div>
        <div>
          <WebPhoneInput
            value={resolvedPhone}
            onChange={(value) => {
              setPhone(value);
              const trimmed = value.trim();
              if (!trimmed || isValidPhone(trimmed)) {
                setPhoneError("");
              }
            }}
            disabled={false}
            label={tAuth("onboarding.cvJourney.phoneLabel", {
              defaultValue: "Phone number",
            })}
            borderColor={displayedPhoneError ? "#d93025" : undefined}
            inputId="edit-cv-phone"
            describedBy={
              displayedPhoneError ? "edit-cv-phone-error" : undefined
            }
            required
            invalid={Boolean(displayedPhoneError)}
          />
          {displayedPhoneError ? (
            <p
              id="edit-cv-phone-error"
              style={{
                margin: "6px 0 0",
                fontSize: 13,
                lineHeight: "18px",
                color: "#d93025",
              }}
            >
              {displayedPhoneError}
            </p>
          ) : null}
        </div>
        <WebLocationPicker
          value={location}
          onSelect={setLocation}
          placeholder={tAuth("onboarding.cvJourney.locationPlaceholder", {
            defaultValue: "Abu Dhabi, UAE",
          })}
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <span style={LABEL}>
          {tAuth("onboarding.cvJourney.links", { defaultValue: "Links" })}
        </span>
        {addedLinks.map((platform) => {
          const linkError = visibleErrors[platform.key];
          return (
            <div key={platform.key}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  borderRadius: 9999,
                  paddingInline: 16,
                  height: 48,
                  border: linkError
                    ? `1px solid ${"#d93025"}`
                    : "1px solid transparent",
                }}
              >
                {platform.icon ? (
                  <span
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 13,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      backgroundColor: platform.brandColor,
                    }}
                  >
                    {platform.icon("#ffffff")}
                  </span>
                ) : (
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--text-tertiary)",
                      width: 20,
                    }}
                  >
                    {platform.label.slice(0, 2)}
                  </span>
                )}
                <span
                  style={{
                    flexShrink: 0,
                    color: "var(--text-secondary)",
                    fontSize: 14,
                    fontWeight: 500,
                  }}
                >
                  {platform.prefix}
                </span>
                <input
                  style={{
                    flex: 1,
                    border: "none",
                    background: "none",
                    outline: "none",
                    fontSize: 15,
                    fontWeight: 500,
                    color: "var(--text-primary)",
                    fontFamily: "inherit",
                    padding: 0,
                  }}
                  value={links[platform.key] || ""}
                  onChange={(event) =>
                    // A pasted full link collapses to the handle after the
                    // fixed prefix.
                    setLinks({
                      ...links,
                      [platform.key]: collapseCvLinkInput(
                        platform.key,
                        event.target.value,
                      ),
                    })
                  }
                  placeholder={platform.placeholder}
                  maxLength={MAX_LINK_URL}
                  aria-invalid={Boolean(linkError)}
                />
                <button
                  type="button"
                  onClick={() => {
                    void confirmDeleteCvItem(t, "link", () =>
                      setLinks({ ...links, [platform.key]: undefined }),
                    );
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 4,
                  }}
                >
                  <X size={16} color="var(--text-tertiary)" />
                </button>
              </div>
              {linkError ? (
                <p
                  style={{
                    margin: "6px 0 0",
                    fontSize: 13,
                    lineHeight: "18px",
                    color: "#d93025",
                  }}
                >
                  {linkError}
                </p>
              ) : null}
            </div>
          );
        })}
        {unaddedLinks.length > 0 ? (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {unaddedLinks.map((platform) => (
              <button
                key={platform.key}
                type="button"
                onClick={() => setLinks({ ...links, [platform.key]: "" })}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  paddingInline: 12,
                  paddingBlock: 8,
                  borderRadius: 12,
                  backgroundColor: "rgba(0,0,0,0.04)",
                  border: "none",
                  cursor: "pointer",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--text-primary)",
                  fontFamily: "inherit",
                  transition: "background-color 150ms",
                }}
              >
                {platform.icon ? (
                  <span
                    style={{
                      width: 22,
                      height: 22,
                      borderRadius: 11,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      backgroundColor: platform.brandColor,
                    }}
                  >
                    {platform.icon("#ffffff")}
                  </span>
                ) : (
                  <Plus size={12} color="var(--text-tertiary)" />
                )}
                {platform.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={isSubmitDisabled}
        style={{
          width: "100%",
          height: 48,
          marginTop: 28,
          borderRadius: 9999,
          backgroundColor: "var(--text-primary)",
          color: "var(--bg)",
          fontSize: 16,
          fontWeight: 600,
          border: "none",
          cursor: isSubmitDisabled ? "not-allowed" : "pointer",
          fontFamily: "inherit",
          opacity:
            isSaving || usernameEditor.isSavingUsername
              ? 0.72
              : isSubmitDisabled
                ? 0.5
                : 1,
        }}
      >
        {isSaving || usernameEditor.isSavingUsername
          ? t("cv.edit.saving", { defaultValue: "Saving..." })
          : t("cv.edit.save")}
      </button>

      <style>{`
        .cv-header-info-button {
          position: relative;
          width: 20px;
          height: 20px;
          border: none;
          background: transparent;
          border-radius: 9999px;
          color: var(--text-secondary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          cursor: help;
          flex: 0 0 auto;
          transition: background-color 150ms, color 150ms;
        }

        .cv-header-info-button:hover,
        .cv-header-info-button:focus-visible {
          background: rgba(0,0,0,0.04);
          color: var(--text-primary);
          outline: none;
        }

        .cv-header-info-button::after {
          content: attr(data-tooltip);
          position: absolute;
          left: 50%;
          bottom: calc(100% + 8px);
          width: min(310px, calc(100vw - 48px));
          transform: translateX(-50%) translateY(4px);
          border: 1px solid var(--input-border);
          border-radius: 12px;
          padding: 10px 12px;
          background: var(--bg);
          box-shadow: 0 16px 40px rgba(0, 0, 0, 0.14);
          color: var(--text-secondary);
          font-size: 12px;
          font-weight: 450;
          line-height: 18px;
          text-align: left;
          white-space: normal;
          opacity: 0;
          pointer-events: none;
          transition: opacity 150ms, transform 150ms;
          z-index: 20;
        }

        .cv-header-info-button::before {
          content: "";
          position: absolute;
          left: 50%;
          bottom: calc(100% + 3px);
          width: 10px;
          height: 10px;
          border-right: 1px solid var(--input-border);
          border-bottom: 1px solid var(--input-border);
          background: var(--bg);
          transform: translateX(-50%) rotate(45deg);
          opacity: 0;
          transition: opacity 150ms;
          z-index: 21;
        }

        .cv-header-info-button:hover::after,
        .cv-header-info-button:focus-visible::after,
        .cv-header-info-button:hover::before,
        .cv-header-info-button:focus-visible::before {
          opacity: 1;
        }

        .cv-header-info-button:hover::after,
        .cv-header-info-button:focus-visible::after {
          transform: translateX(-50%) translateY(0);
        }
      `}</style>
    </div>
  );
}
